import { useState, useCallback, useMemo, useRef, useEffect, type RefObject } from 'react'
import { useIntl } from 'react-intl'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'
import IconButton from 'LuxKit/Button/IconButton'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import Group from 'components/utils/Group'
import noop from 'lib/function/noop'
import useRebrandLightSurfaceButtonVariant from 'hooks/useRebrandLightSurfaceButtonVariant'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'
import type { MessageItem } from 'tripPlanner/components/Chat/MessagesList'
import { getTextFromParts } from 'tripPlanner/utils/messageParts'
import type { DrawerSessionPair } from 'tripPlanner/pages/chat/ChatTripPlannerPage/hooks/useDrawerAskAI'
import { stripContextTags } from 'tripPlanner/utils/contextTag'
import { useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { getChatLinkContentKey, useChatLinkInteractions } from 'tripPlanner/components/Chat/useChatLinkInteractions'
import ChatLoadingIndicator from 'tripPlanner/components/Chat/ChatLoadingIndicator'
import type { ChatStreamStatusValue } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import DrawerAskAIResponse from './DrawerAskAIResponse'
import { buildPlaceDrawerOpenedActivityEntry } from 'tripPlanner/pages/chat/ChatTripPlannerPage/containers/buildPlaceDrawerOpenedActivityEntry'

function getUserDisplayText(message: MessageItem): string {
  return stripContextTags(getTextFromParts(message.parts) || message.rawContent || '')
}

const BUBBLE_PADDING = 16
const EMPTY_FAILED_PLACE_QUERIES = new Set<string>()

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(${rem(8)}); }
  to { opacity: 1; transform: translateY(0); }
`

const Container = styled.div`
  animation: ${fadeIn} 0.2s ease-out;
`

const Bubble = styled.div`
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${rem(16)};
  box-shadow:
    0 ${rem(10)} ${rem(15)} ${rem(-3)} rgba(0, 0, 0, 0.1),
    0 ${rem(4)} ${rem(6)} ${rem(-4)} rgba(0, 0, 0, 0.1);
  overflow: hidden;
`

const BubbleHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${rem(8)};
  padding-block-start: ${rem(8)};
  padding-inline: ${rem(12)};
`

const BubbleContent = styled.div`
  padding-block-end: ${rem(BUBBLE_PADDING)};
  padding-inline: ${rem(BUBBLE_PADDING)};
  max-height: ${rem(350)};
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
`

const Transcript = styled.div`
  min-height: 0;
  overflow-y: auto;
`

const Progress = styled.div`
  flex-shrink: 0;
`

const UserMessageRow = styled.div`
  display: flex;
  justify-content: flex-end;
`

const UserBubble = styled.div`
  max-width: 75%;
  padding-block: ${rem(8)};
  padding-inline: ${rem(18)};
  border-radius: ${rem(18)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  overflow-wrap: break-word;
  white-space: pre-wrap;

  /*
   * Under rebrand the bubble behind this sits on Light Surface, so a user
   * message filled with the same token disappears. Step it up to Light Surface
   * ALT, matching the main chat's user bubble in Chat/Message.tsx.
   */
  body.${REBRAND_PALETTE_CLASS} & {
    background-color: ${props => props.theme.palette.neutral.surface.lightBackgroundAlt};
    color: ${props => props.theme.palette.neutral.surface.lightContrastAlt};
  }
`

interface Props {
  latestResponse: MessageItem | undefined
  sessionHistory: Array<DrawerSessionPair>
  isResponseStreaming: boolean
  isWaitingForResponse: boolean
  progress: Pick<ChatStreamStatusValue, 'loadingState' | 'reasoningSummaries' | 'toolCallInfo' | 'isOnline'>
  onOpenFullChat?: () => void
  onSendMessage?: (message: string) => void
  dismissOnBackgroundClick?: boolean
  clickAwayBoundaryRef?: RefObject<HTMLElement | null>
  onVisibilityChange?: (isVisible: boolean) => void
}

function DrawerAskAIBubble({
  latestResponse,
  sessionHistory,
  isResponseStreaming,
  isWaitingForResponse,
  progress,
  onOpenFullChat = noop,
  onSendMessage = noop,
  dismissOnBackgroundClick = false,
  clickAwayBoundaryRef,
  onVisibilityChange = noop,
}: Props) {
  const intl = useIntl()
  const lightSurfaceButtonVariant = useRebrandLightSurfaceButtonVariant()
  const eventBus = useTripPlannerEventBus()
  const [closedQuestionId, setClosedQuestionId] = useState<string>()
  const latestQuestionId = sessionHistory.at(-1)?.question.id ?? latestResponse?.id
  const isClosed = closedQuestionId !== undefined && closedQuestionId === latestQuestionId
  const showProgress = (isWaitingForResponse || isResponseStreaming) && progress.loadingState.isListLoading
  const isVisible = !isClosed && (isWaitingForResponse || !!latestResponse || sessionHistory.length > 0)
  const bubbleRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const markdownContainerRef = useRef<HTMLDivElement>(null)

  const openFullChatLabel = intl.formatMessage({
    id: '7gT37mFbFhgaYeK7tqhQ',
    defaultMessage: 'Open full chat',
    description: 'Link that opens the full Trip Planner chat tab from a floating Ask AI preview',
  })
  const closeBubbleLabel = intl.formatMessage({
    id: '3j7IQmGegtc_a7v3bKD7',
    defaultMessage: 'Close bubble',
    description: 'Aria label for button to close the AI assistant chat bubble',
  })

  const handleClose = useCallback(() => {
    setClosedQuestionId(latestQuestionId)
  }, [latestQuestionId])

  useEffect(() => {
    onVisibilityChange(isVisible)
    return () => onVisibilityChange(false)
  }, [isVisible, onVisibilityChange])

  useEffect(() => {
    if (!dismissOnBackgroundClick || !isVisible) return

    const handleBackgroundClick = (event: MouseEvent) => {
      const boundary = clickAwayBoundaryRef?.current ?? bubbleRef.current
      if (boundary && !boundary.contains(event.target as Node)) {
        handleClose()
      }
    }

    document.addEventListener('click', handleBackgroundClick, true)
    return () => document.removeEventListener('click', handleBackgroundClick, true)
  }, [clickAwayBoundaryRef, dismissOnBackgroundClick, handleClose, isVisible])

  const handleOpenFullChat = useCallback(() => {
    onOpenFullChat()
  }, [onOpenFullChat])

  const handleOfferClick = useCallback((data: { offerId: string; offerType?: string }) => {
    eventBus.dispatch({ type: 'open_offer_drawer', ...data })
  }, [eventBus])

  const handlePlaceClick = useCallback((data: { googlePlaceId: string; placeName: string }) => {
    eventBus.dispatch({ type: 'open_place_drawer', ...data })
    eventBus.dispatch({
      type: 'trip_planner_surface_activity',
      entries: [buildPlaceDrawerOpenedActivityEntry(data.placeName, 'trip_planner.drawer.ask_ai_bubble', data.googlePlaceId)],
    })
  }, [eventBus])

  const handlePlaceSearchClick = useCallback((data: { searchQuery: string; placeName: string }) => {
    eventBus.dispatch({ type: 'open_place_search_drawer', ...data })
  }, [eventBus])

  const linkContentKey = useMemo(() => {
    const parts = sessionHistory.flatMap(pair => [
      ...pair.question.parts,
      ...(pair.answer?.parts ?? []),
    ])
    if (sessionHistory.length === 0 && latestResponse) {
      parts.push(...latestResponse.parts)
    }
    return getChatLinkContentKey(parts)
  }, [latestResponse, sessionHistory])

  useChatLinkInteractions({
    markdownContainerRef,
    canShowHover: false,
    contentKey: linkContentKey,
    failedPlaceQueries: EMPTY_FAILED_PLACE_QUERIES,
    onOfferClick: handleOfferClick,
    onPlaceClick: handlePlaceClick,
    onPlaceSearchClick: handlePlaceSearchClick,
  })

  useEffect(() => {
    if (isResponseStreaming && contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight
    }
  }, [isResponseStreaming, latestResponse?.parts])

  if (!isVisible) {
    return null
  }

  return (
    <Container ref={bubbleRef} data-testid="drawer-ask-ai-bubble">
      <Bubble>
        <BubbleHeader>
          <TextLink size="small" onClick={handleOpenFullChat}>
            {openFullChatLabel}
          </TextLink>
          <IconButton
            kind="tertiary"
            variant={lightSurfaceButtonVariant}
            size="small"
            onClick={handleClose}
            aria-label={closeBubbleLabel}
          >
            <LineTimesIcon />
          </IconButton>
        </BubbleHeader>
        <BubbleContent>
          <Transcript ref={contentRef}>
            <div ref={markdownContainerRef}>
              <Group direction="vertical" gap={24}>
                {sessionHistory.map((pair) => {
                  const userText = getUserDisplayText(pair.question)
                  return (
                    <Group direction="vertical" gap={8} key={pair.question.id}>
                      {userText && (
                        <UserMessageRow>
                          <UserBubble>
                            <BodyText variant="medium">
                              {userText}
                            </BodyText>
                          </UserBubble>
                        </UserMessageRow>
                      )}
                      {pair.answer && <DrawerAskAIResponse message={pair.answer} onSendMessage={onSendMessage} />}
                    </Group>
                  )
                })}
                {sessionHistory.length === 0 && latestResponse && <DrawerAskAIResponse message={latestResponse} onSendMessage={onSendMessage} />}
              </Group>
            </div>
          </Transcript>
          {showProgress && (
            <Progress data-testid="drawer-ask-ai-progress">
              <ChatLoadingIndicator
                isUpdatingItinerary={progress.loadingState.isUpdatingItinerary}
                activeToolLoadingKey={progress.loadingState.activeToolLoadingKey}
                loadingStatus={progress.loadingState.status}
                reasoningSummaries={progress.reasoningSummaries}
                toolCallInfo={progress.toolCallInfo}
                isOnline={progress.isOnline}
              />
            </Progress>
          )}
        </BubbleContent>
      </Bubble>
    </Container>
  )
}

DrawerAskAIBubble.displayName = 'DrawerAskAIBubble'

export default DrawerAskAIBubble
